import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Phone, Sun, Moon, Sparkles } from 'lucide-react'
import { useTheme } from '@/hooks/useTheme'
import { useScrollPosition } from '@/hooks/useScrollPosition'

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Gallery', href: '/gallery' },
  { label: 'Services', href: '/services' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'About', href: '/about' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/contact' },
]

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const { isDark, toggle } = useTheme()
  const { isScrolled } = useScrollPosition()
  const location = useLocation()

  const isActive = (href: string) => location.pathname === href

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'glass-strong shadow-lg py-3'
            : 'bg-transparent py-5'
        }`}
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 group"
            aria-label="NailArtPro Home"
          >
            <Sparkles className="w-7 h-7 text-luxury-champagne group-hover:rotate-12 transition-transform" />
            <span className="font-display text-2xl font-semibold tracking-tight">
              Nail<span className="text-luxury-champagne">Art</span>Pro
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" role="navigation" aria-label="Main navigation">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  isActive(link.href)
                    ? 'text-luxury-champagne bg-luxury-champagne/10'
                    : 'hover:text-luxury-champagne hover:bg-luxury-champagne/5'
                }`}
                aria-current={isActive(link.href) ? 'page' : undefined}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggle}
              className="p-2.5 rounded-full hover:bg-luxury-champagne/10 transition-colors"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <a
              href="tel:+919088066676"
              className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-luxury-champagne/30 text-luxury-champagne hover:bg-luxury-champagne/10 transition-all text-sm font-medium"
            >
              <Phone className="w-4 h-4" />
              Call Now
            </a>
            <a
              href="https://wa.me/919088066676?text=Hi%20NailArtPro%2C%20I%20would%20like%20to%20book%20an%20appointment"
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2.5 rounded-full bg-luxury-champagne text-luxury-black font-semibold text-sm hover:bg-luxury-rose transition-colors shadow-lg shadow-luxury-champagne/20"
            >
              Book Appointment
            </a>
          </div>

          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={toggle}
              className="p-2 rounded-full hover:bg-luxury-champagne/10 transition-colors"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 rounded-full hover:bg-luxury-champagne/10 transition-colors"
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 pt-20 glass-strong"
          >
            <nav className="flex flex-col items-center gap-2 p-6" role="navigation" aria-label="Mobile navigation">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`w-full max-w-xs text-center px-6 py-3 rounded-xl text-lg font-medium transition-all ${
                    isActive(link.href)
                      ? 'text-luxury-champagne bg-luxury-champagne/10'
                      : 'hover:text-luxury-champagne hover:bg-luxury-champagne/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-3 mt-6 w-full max-w-xs">
                <a
                  href="tel:+919088066676"
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-luxury-champagne/30 text-luxury-champagne font-medium"
                >
                  <Phone className="w-4 h-4" />
                  9088066676
                </a>
                <a
                  href="https://wa.me/919088066676?text=Hi%20NailArtPro%2C%20I%20would%20like%20to%20book%20an%20appointment"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center px-6 py-3 rounded-full bg-luxury-champagne text-luxury-black font-semibold"
                >
                  Book Appointment
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
